import { useEffect, type ReactNode } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useAuthBootstrap } from '@foundation-bb/foundation-auth';
import type { AppDispatch } from '@/store/store';
import {
  refreshToken,
  setAuthLoading,
  selectAccessToken,
  selectAuthUser,
  selectIsAuthenticated,
} from '@/store/authSlice';
import { setAuthenticatedUser, clearAuthenticatedUser } from '@/telemetry';

// Non-HttpOnly session hint cookie. Scribia's backend overrides Foundation's
// default (`fdn_has_session`) via the infra app setting
// `Auth__Cookie__HintName = scribia_has_session` (binds Foundation's
// RefreshCookieOptions from section `Auth:Cookie`). The bootstrap gates the
// cold-load refresh on this cookie's presence, so the name MUST match what the
// server sets — otherwise a hard refresh finds no hint and logs the user out.
const HINT_COOKIE_NAME = 'scribia_has_session';

interface AuthManagerProps {
  children: ReactNode;
}

export default function AuthManager({ children }: AuthManagerProps) {
  const dispatch = useDispatch<AppDispatch>();
  const accessToken = useSelector(selectAccessToken);
  const user = useSelector(selectAuthUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);

  // Cold-load bootstrap + proactive pre-expiry refresh (replaces the bespoke
  // startup-refresh + proactive-refresh effects).
  useAuthBootstrap({
    hintCookieName: HINT_COOKIE_NAME,
    getAccessToken: () => accessToken,
    refresh: () => dispatch(refreshToken()),
    setLoadingDone: () => dispatch(setAuthLoading(false)),
  });

  // Telemetry: sync authenticated user context
  useEffect(() => {
    if (isAuthenticated && user) {
      setAuthenticatedUser(user.id, user.tenantId);
    } else {
      clearAuthenticatedUser();
    }
  }, [isAuthenticated, user]);

  return <>{children}</>;
}
